import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SiteList from './SiteList';
import SiteForm from './SiteForm';
import ConnectionWizard from './ConnectionWizard';
import { UserAccessManager } from '../UserAccess';
import {
  getAllSites,
  getCustomers,
  createSite,
  updateSite,
  deleteSite,
  retrySiteConnection,
  syncSiteCameras,
  testSiteConnection,
} from 'api/canvms';

function SiteManager({ open, onClose, onSitesChanged, currentUserId }) {
  // Sites state
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(null);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Edit form state
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Access manager state
  const [accessManagerOpen, setAccessManagerOpen] = useState(false);
  const [accessManagerSite, setAccessManagerSite] = useState(null);

  // Action state
  const [testingId, setTestingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  // Notification state
  const [notification, setNotification] = useState(null);

  const fetchSites = useCallback(async () => {
    try {
      setSitesLoading(true);
      setSitesError(null);
      const response = await getAllSites();
      setSites(response.data?.sites || []);
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error fetching sites:', err);
      setSitesError(err.response?.data?.message || err.message || 'Failed to fetch sites');
    } finally {
      setSitesLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setCustomersLoading(true);
      const response = await getCustomers();
      setCustomers(response.data?.customers || []);
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error fetching customers:', err);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSites();
      fetchCustomers();
    }
  }, [open, fetchSites, fetchCustomers]);

  const showNotification = (message, severity = 'success') => {
    setNotification({ message, severity });
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  // Edit handlers
  const handleEdit = (site) => {
    setEditingSite(site);
    setSaveError(null);
    setEditFormOpen(true);
  };

  const handleEditClose = () => {
    setEditFormOpen(false);
    setEditingSite(null);
    setSaveError(null);
  };

  const handleEditSave = async (siteId, data) => {
    try {
      setSaving(true);
      setSaveError(null);
      await updateSite(siteId, data);
      showNotification('Site updated successfully');
      handleEditClose();
      await fetchSites();
      onSitesChanged?.();
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error updating site:', err);
      setSaveError(err.response?.data?.message || err.message || 'Failed to update site');
    } finally {
      setSaving(false);
    }
  };

  // Create handlers
  const handleOpenWizard = () => {
    setCreateError(null);
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setCreateError(null);
  };

  const handleTestConnection = async (connectionData) => {
    const response = await testSiteConnection(connectionData);
    return response.data;
  };

  const handleCreate = async (data) => {
    try {
      setCreating(true);
      setCreateError(null);
      await createSite(data);
      showNotification('Site created successfully');
      handleCloseWizard();
      await fetchSites();
      onSitesChanged?.();
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error creating site:', err);
      setCreateError(err.response?.data?.message || err.message || 'Failed to create site');
    } finally {
      setCreating(false);
    }
  };

  // Delete handler
  const handleDelete = async (siteId) => {
    try {
      await deleteSite(siteId);
      showNotification('Site deleted successfully');
      await fetchSites();
      onSitesChanged?.();
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error deleting site:', err);
      showNotification(
        err.response?.data?.message || err.message || 'Failed to delete site',
        'error'
      );
    }
  };

  // Test connection handler
  const handleRetryConnection = async (siteId) => {
    try {
      setTestingId(siteId);
      const response = await retrySiteConnection(siteId);
      const { new_status, test_result } = response.data;

      if (test_result.success) {
        showNotification(`Connection successful! Status: ${new_status}`);
      } else {
        showNotification(`Connection failed: ${test_result.error}`, 'error');
      }

      await fetchSites();
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error testing connection:', err);
      showNotification(
        err.response?.data?.message || err.message || 'Connection test failed',
        'error'
      );
    } finally {
      setTestingId(null);
    }
  };

  // Sync cameras handler
  const handleSyncCameras = async (siteId) => {
    try {
      setSyncingId(siteId);
      const response = await syncSiteCameras(siteId);
      const { synced, errors } = response.data;

      if (errors > 0) {
        showNotification(`Synced ${synced} cameras with ${errors} errors`, 'warning');
      } else {
        showNotification(`Synced ${synced} cameras successfully`);
      }

      await fetchSites();
      onSitesChanged?.();
    } catch (err) {
      console.error('[CANVMS/SiteManager] Error syncing cameras:', err);
      showNotification(
        err.response?.data?.message || err.message || 'Failed to sync cameras',
        'error'
      );
    } finally {
      setSyncingId(null);
    }
  };

  // Manage access handler
  const handleManageAccess = (site) => {
    setAccessManagerSite(site);
    setAccessManagerOpen(true);
  };

  const handleCloseAccessManager = () => {
    setAccessManagerOpen(false);
    setAccessManagerSite(null);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', md: 900 }, maxWidth: '100%' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              Site Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSites}
              disabled={sitesLoading}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenWizard}
            >
              Add Site
            </Button>
            <IconButton onClick={onClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
            {sitesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {sitesError}
              </Alert>
            )}

            <SiteList
              sites={sites}
              loading={sitesLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTestConnection={handleRetryConnection}
              onSyncCameras={handleSyncCameras}
              onManageAccess={handleManageAccess}
              testingId={testingId}
              syncingId={syncingId}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Edit Form Dialog */}
      <SiteForm
        open={editFormOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        site={editingSite}
        customers={customers}
        customersLoading={customersLoading}
        saving={saving}
        error={saveError}
      />

      {/* Connection Wizard Dialog */}
      <ConnectionWizard
        open={wizardOpen}
        onClose={handleCloseWizard}
        onCreate={handleCreate}
        onTestConnection={handleTestConnection}
        customers={customers}
        customersLoading={customersLoading}
        creating={creating}
        error={createError}
      />

      {/* User Access Manager Dialog */}
      <UserAccessManager
        open={accessManagerOpen}
        onClose={handleCloseAccessManager}
        siteId={accessManagerSite?.id}
        siteName={accessManagerSite?.display_name || accessManagerSite?.name}
        currentUserId={currentUserId}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notification && (
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}

export default SiteManager;
